export interface RelationConfig {
  [key: string]: {
    type?: new (...args: any[]) => any;
    transform?: (value: any) => any;
    serialize?: (value: any) => any;
  };
}

export abstract class BaseModel {
  // Allow dynamic property access for DataGrid compatibility
  [key: string]: any;

  constructor(data?: any) {
    const relations = (this.constructor as any).relations?.() || {};

    if (data) {
      for (const [key, value] of Object.entries(data)) {
        // Skip getter-only properties defined on the prototype
        const descriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(this), key);
        if (descriptor?.get && !descriptor?.set) {
          continue;
        }

        if (relations[key]) {
          const config = relations[key];

          if (config.transform) {
            // Custom transform takes highest priority (handles DateYMD, TimeMinute, Date, etc.)
            if (Array.isArray(value)) {
              (this as any)[key] = value.map((item: any) => config.transform!(item));
            } else {
              (this as any)[key] = config.transform(value);
            }
          } else if (config.type) {
            // Model relation hydration
            if (Array.isArray(value)) {
              (this as any)[key] = value.map((item: any) => new config.type!(item));
            } else if (value != null && typeof value === 'object') {
              (this as any)[key] = new config.type!(value);
            } else {
              (this as any)[key] = value;
            }
          } else {
            (this as any)[key] = value;
          }
        } else {
          (this as any)[key] = value;
        }
      }
    }
  }

  static relations(): RelationConfig {
    return {};
  }

  objectID(): string | null {
    return null;
  }

  get isNewRecord(): boolean {
    const objectID = this.objectID();

    if (!objectID) {
      return true;
    }

    const numericID = Number(objectID);

    // Legacy `/new` responses can serialize an absent numeric primary key as 0.
    return Number.isFinite(numericID) && numericID <= 0;
  }

  get display(): string {
    return (this as any).displayName || (this as any).name || (this as any).value || (this as any).title;
  }

  get allow(): Record<string, boolean> {
    const actions = (this as any).permissions?.actions;

    if (actions) {
      return actions.reduce((object: Record<string, boolean>, action: string) => {
        return {
          ...object,
          [action]: true,
        };
      }, {});
    }

    return {};
  }

  copy(): this {
    return Object.assign(
      Object.create(Object.getPrototypeOf(this)),
      this
    );
  }

  clone(): this {
    return new (this.constructor as new (data?: Record<string, any>) => this)(
      structuredClone(this.toJSON())
    );
  }

  toJSON(): Record<string, any> {
    const relations = (this.constructor as any).relations?.() || {};
    const result: Record<string, any> = {};

    for (const key of Object.keys(this)) {
      const value = (this as any)[key];
      const config = relations[key];

      if (config?.serialize) {
        // Custom serializer (handles DateYMD → integer, TimeMinute → integer, Date → timestamp, etc.)
        if (Array.isArray(value)) {
          result[key] = value.map((item: any) => config.serialize!(item));
        } else {
          result[key] = config.serialize(value);
        }
      } else if (value instanceof BaseModel) {
        result[key] = value.toJSON();
      } else if (Array.isArray(value)) {
        result[key] = value.map((item: any) =>
          item instanceof BaseModel ? item.toJSON() : item
        );
      } else {
        result[key] = value;
      }
    }

    return result;
  }
}
